import React, { useMemo, useCallback, useContext } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import moment from 'moment'

import { CruiseContentSections, CRUISE_CONTENT_SECTION_LABELS } from 'constants/cruise'
import { sortBy } from 'lib/array/arrayUtils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalContext from 'contexts/ModalContext'

import TabButtonsGroup from 'components/Luxkit/Tabs/TabButtonsGroup'
import TabButton from 'components/Luxkit/Tabs/TabButton'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import BodyText from 'components/Luxkit/Typography/BodyText'
import CruiseSoldoutDepartureCard from 'components/OfferPage/Cruises/CruiseSoldoutDepartureCard'
import CruiseSoldoutStillInterested from 'components/OfferPage/Cruises/CruiseSoldoutStillInterested'
import Modal from 'components/Luxkit/Modal/Modal'
import { InView } from 'react-intersection-observer'
import { scrollToOfferPackageOptions, scrollToOfferPackageOptionsOfferType } from 'analytics/optimizely/events'
import * as Analytics from 'analytics/analytics'

import CruiseDepartureDatesTabs from './CruiseDepartureDatesTabs'
import MultipleCabinsCallUs from '../MultipleCabinsCallUs'
import CruiseDepartures from './CruiseDepartures'
import ScrollAnchor from 'components/Common/ScrollAnchor'

const SectionContainer = styled(LayoutContainer)`
  padding-bottom: ${rem(32)};
  padding-top: ${rem(32)};
`

const YearTabButtonsGroup = styled(TabButtonsGroup)`
  ${mediaQueryUp.tablet} {
    margin-top: ${rem(16)};
    width: fit-content;
    max-width: ${rem(424)};
  }
`

export const hasDepartureThisYear = (departures: DeparturesByDate, year: string): boolean => {
  return Object.keys(departures)
    .filter(departureDate => departureDate.includes(year))?.length > 0
}

const CABIN_INFO_DESCRIPTION: { [key in App.CabinCategoryType]: string } = {
  Inside: 'An inside cabin is normally the most affordable option, often without a window.',
  Oceanview: 'An oceanview cabin, often called an outside cabin, is a room with a port hole or window that looks out to sea, depending on deck and location.',
  Balcony: 'A balcony cabin is any cruise room with a private verandah, allowing you to take in the beautiful views and fresh sea air.',
  Suite: 'A suite is an especially large room within a cruise ship\'s top tier of cabins, often with a wide window and/or private verandah.',
}

export type DeparturesByDate = {
  [date: string]: App.CruiseDepartureDetails[]
}

interface Props {
  offer: App.CruiseOffer;
  selectedDepartureYear: string;
  departureYears: Set<string>;
  departures: DeparturesByDate;
  monthTab: string;
  hasValidDepartures: boolean;
  setYear: (year: string) => void;
  setMonthTab: (tab: string) => void;
  onHandleSelectDeparture: (departure: App.CruiseDepartureDetails) => void;
}

function CruiseOfferPageDepartures({
  offer,
  selectedDepartureYear,
  departureYears,
  departures,
  monthTab,
  hasValidDepartures,
  setYear,
  setMonthTab,
  onHandleSelectDeparture,
}: Props) {
  const { mainDepartureDetails } = offer
  const { lowestPriceDetailsByCategory } = mainDepartureDetails

  const showModal = useContext(ModalContext)

  const handleYearChange = useCallback((departures: DeparturesByDate, year: string) => {
    const departureKey = Object.keys(departures)
      .find((key) => key.includes(year))

    setYear(String(year))
    if (departureKey) setMonthTab(departureKey)
  }, [setYear, setMonthTab])

  const openCabinInfoModal = useCallback((cabinType: App.CabinCategoryType) => {
    showModal(<Modal title={cabinType}>
      <BodyText weight="bold" variant="large">
        About this cabin
      </BodyText>
      {!!cabinType && CABIN_INFO_DESCRIPTION[cabinType]}
    </Modal>)
  }, [showModal])

  const handlePackageOptionsInView = useCallback((inView: boolean) => {
    if (inView) {
      Analytics.track('scroll-to-offer-package-options', scrollToOfferPackageOptions())
      Analytics.track('scroll-to-offer-package-options', scrollToOfferPackageOptionsOfferType(offer.type))
    }
  }, [offer])

  const month = useMemo(() => moment(monthTab).format('MMMM YYYY'), [monthTab])

  const tabDepartures = useMemo(() => {
    return sortBy(
      (departures[monthTab] || []),
      ({ departureDate }) => departureDate ? new Date(departureDate).getTime() : 0,
      'asc',
    )
  }, [departures, monthTab])

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return [...Array(5).keys()].map(i => String(currentYear + i))
  }, [])

  const availableMonths = useMemo(
    () => Object.keys(departures).filter((i) => i.includes(selectedDepartureYear)),
    [departures, selectedDepartureYear],
  )

  return <Pane className="type-light-grey">
    <ScrollAnchor id="departures-anchor"/>
    <SectionContainer size="md">
      <Heading variant="heading2" align="center" className="section-title">
        {CRUISE_CONTENT_SECTION_LABELS[CruiseContentSections.DEPARTURES]}
      </Heading>

      <Group direction="vertical" gap={32}>
        {departureYears.size > 1 && <YearTabButtonsGroup fit="grow-at-mobile">
          {years.map((year) => hasDepartureThisYear(departures, year) &&
            <TabButton
              key={year}
              size="medium"
              isActive={String(year) === selectedDepartureYear}
              onClick={() => handleYearChange(departures, year)}
            >{year}</TabButton>,
          )}
        </YearTabButtonsGroup>}

        <CruiseDepartureDatesTabs
          tab={monthTab}
          setTab={setMonthTab}
          departureDate={departures}
          year={selectedDepartureYear}
          cachedDepartures={lowestPriceDetailsByCategory}
        />

        {monthTab && availableMonths.length > 1 && <BodyText variant="large" weight="bold" align="start">{month}</BodyText>}

        {monthTab && tabDepartures.map((departure, index) => <>
          <CruiseDepartures
            cruise={offer}
            key={departure.id}
            departure={departure}
            onCabinInfoModal={openCabinInfoModal}
            onHandleDepartureModal={onHandleSelectDeparture}
          />
          {index === 0 && <InView rootMargin="0px" triggerOnce onChange={handlePackageOptionsInView} />}
        </>,
        )}
        {!hasValidDepartures && <CruiseSoldoutDepartureCard />}
        {!hasValidDepartures && <CruiseSoldoutStillInterested useLinkButton />}
        {hasValidDepartures && <MultipleCabinsCallUs />}
      </Group>
    </SectionContainer>
  </Pane>
}

export default CruiseOfferPageDepartures
